import { FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCommunicationRealtime,
  useConversationRead,
  useConversationMessages,
  useConversationComposer,
  useData,
  useSession,
} from '@yingbaba/api';
import {
  Button,
  ButtonLink,
  Avatar,
  CommunicationAttachment,
  CommunicationConnectionNotice,
  Empty,
  ErrorNotice,
  PageTitle,
  QueryState,
  SupportCaseSummary,
  useToast,
  useConversationScroll,
} from '@yingbaba/ui';
import { ArrowLeft, Upload, MessageSquare, Send } from '@yingbaba/icons';
import { datetime, siteURL, supportOrderURL } from '@yingbaba/utils';
import type { ConversationView, SupportCaseView } from '@yingbaba/types';

function conversationKind(type: string) {
  switch (type) {
    case 'demand':
      return '需求沟通';
    case 'project':
      return '项目群';
    case 'market_inquiry':
      return '资源沟通';
    case 'customer_service':
      return '平台客服';
    default:
      return '业务沟通';
  }
}

export function Messages() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const listPath = query ? `/conversations?${new URLSearchParams({ q: query })}` : '/conversations';
  const conversations = useData<ConversationView[]>(listPath);
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 780px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 780px)');
    const change = () => setMobile(media.matches);
    change();
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  const selectedID =
    params.get('conversation') || (!mobile ? conversations.data?.[0]?.id : '') || '';
  const listed = conversations.data?.find((item) => item.id === selectedID);
  const detail = useData<ConversationView>(
    `/conversations/${selectedID}`,
    !!selectedID && !listed && conversations.isSuccess,
  );
  const selected = listed || detail.data;
  const supportCase = useData<SupportCaseView>(
    `/conversations/${selectedID}/support-case`,
    selected?.type === 'customer_service',
  );
  function updateParams(values: Record<string, string>) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const [key, value] of Object.entries(values))
        value ? next.set(key, value) : next.delete(key);
      return next;
    });
  }
  const messageFeed = useConversationMessages(
    selectedID ? `/conversations/${selectedID}/messages` : '/conversations/none/messages',
    !!selected,
    100,
  );
  const messages = messageFeed.latest;
  const session = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const { text, setText, sending, uploading, error, sendText, attach } = useConversationComposer({
    conversationID: selectedID,
    accountID: session.data?.account.id || '',
    canSend: !!selected,
    listPath,
  });
  const connection = useCommunicationRealtime({
    accountID: session.data?.account.id || '',
    withOrganization: true,
    onEvent: (envelope) => {
      if (
        !['message.created', 'read.updated', 'connected', 'support.updated'].includes(envelope.type)
      )
        return;
      const id = envelope.payload?.conversation_id;
      void qc.invalidateQueries({
        predicate: (query) => {
          const path = String(query.queryKey[0]);
          if (envelope.type === 'support.updated')
            return !!id && path === `/conversations/${id}/support-case`;
          if (path === '/conversations' || path.startsWith('/conversations?')) return true;
          return envelope.type === 'connected'
            ? path.startsWith('/conversations/')
            : !!id && (path === `/conversations/${id}` || path.startsWith(`/conversations/${id}/`));
        },
      });
    },
  });
  useConversationRead({
    accountID: session.data?.account.id || '',
    path: `/conversations/${selectedID}/read`,
    seq: messageFeed.items.at(-1)?.seq,
    enabled: !!selectedID && !!selected,
    onRead: () =>
      void qc.invalidateQueries({
        predicate: (query) =>
          String(query.queryKey[0]).startsWith('/conversations') &&
          !String(query.queryKey[0]).includes('/messages'),
      }),
  });

  const { viewportRef, onScroll, loadEarlier } = useConversationScroll(
    selectedID,
    messageFeed.items,
    session.data?.account.id || '',
    messageFeed.loadOlder,
  );

  function send(event: FormEvent) {
    event.preventDefault();
    void sendText();
  }

  async function sendAttachment(file?: File) {
    if (await attach(file)) toast('附件已发送');
  }

  return (
    <>
      <PageTitle
        title="沟通中心"
        description="与客户、团队和资源方保持沟通，集中查看业务消息与附件。"
      />
      <ErrorNotice error={error || messageFeed.error} />
      <CommunicationConnectionNotice state={connection} />
      <QueryState query={conversations}>
        {!conversations.data?.length && !query && !selectedID ? (
          <Empty
            title="还没有业务会话"
            description="在需求、项目或影视资源详情中点击“沟通”，即可开始对话。"
          />
        ) : (
          <div
            className={`communication-shell work-communication ${selectedID ? 'has-conversation' : ''}`}
          >
            <aside className="communication-list" aria-label="会话列表">
              <form
                className="communication-search"
                key={query}
                role="search"
                aria-label="查找会话"
                onSubmit={(event) => {
                  event.preventDefault();
                  const value = String(new FormData(event.currentTarget).get('q') || '').trim();
                  updateParams({ q: value, conversation: '' });
                }}
              >
                <input
                  className="input"
                  name="q"
                  aria-label="搜索会话名称"
                  placeholder="搜索会话名称"
                  defaultValue={query}
                  maxLength={200}
                />
                <Button type="submit" variant="secondary" size="sm">
                  搜索
                </Button>
              </form>
              {!conversations.data?.length && (
                <div className="communication-empty">没有找到会话，试试其他名称。</div>
              )}
              {conversations.data?.map((conversation) => (
                <button
                  type="button"
                  key={conversation.id}
                  className={`communication-conversation ${conversation.id === selectedID ? 'active' : ''}`}
                  aria-current={conversation.id === selectedID ? 'true' : undefined}
                  onClick={() => updateParams({ conversation: conversation.id })}
                >
                  <div className="row spread">
                    <span className="communication-kind">
                      {conversationKind(conversation.type)}
                    </span>
                    {conversation.unread_count > 0 ? (
                      <span className="communication-unread">
                        {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
                      </span>
                    ) : null}
                  </div>
                  <strong>{conversation.title || '业务会话'}</strong>
                  <p>{conversation.last_message?.content || '开始沟通'}</p>
                  <time>
                    {conversation.last_message
                      ? datetime(conversation.last_message.created_at)
                      : ''}
                  </time>
                </button>
              ))}
            </aside>
            <section className="communication-panel" aria-label="消息内容">
              {selected ? (
                <>
                  <header className="communication-header">
                    <Button
                      variant="ghost"
                      className="communication-back"
                      onClick={() => updateParams({ conversation: '' })}
                    >
                      <ArrowLeft size={17} />
                      会话
                    </Button>
                    <div>
                      <span className="eyebrow">{conversationKind(selected.type)}</span>
                      <h2>{selected.title}</h2>
                    </div>
                    {selected.type === 'project' && selected.business_id ? (
                      <ButtonLink variant="secondary" to={`/projects/${selected.business_id}`}>
                        查看项目
                      </ButtonLink>
                    ) : selected.type === 'demand' && selected.business_id ? (
                      <ButtonLink variant="secondary" to="/demands">
                        查看需求
                      </ButtonLink>
                    ) : selected.resource_id ? (
                      <ButtonLink
                        variant="secondary"
                        to={siteURL('www', `/resources/${selected.resource_id}`)}
                      >
                        查看关联资源
                      </ButtonLink>
                    ) : (
                      <MessageSquare size={20} />
                    )}
                  </header>
                  {selected.type === 'customer_service' && (
                    <>
                      <ErrorNotice error={supportCase.error} />
                      {supportCase.isSuccess && supportCase.data?.id && (
                        <SupportCaseSummary
                          value={supportCase.data}
                          orderLink={supportOrderURL(supportCase.data)}
                        />
                      )}
                    </>
                  )}
                  <QueryState query={messages}>
                    <div
                      className="communication-messages"
                      ref={viewportRef}
                      onScroll={onScroll}
                      aria-live="polite"
                    >
                      {messageFeed.hasMore ? (
                        <Button
                          variant="secondary"
                          loading={messageFeed.loadingOlder}
                          onClick={() => void loadEarlier()}
                        >
                          加载更早消息
                        </Button>
                      ) : null}
                      {messageFeed.items.length ? (
                        messageFeed.items.map((message) => {
                          const mine = message.sender_id === session.data?.account.id;
                          return (
                            <article
                              className={`communication-message ${mine ? 'mine' : ''}`}
                              key={message.id}
                            >
                              <div className="communication-message-meta">
                                <Avatar
                                  name={message.sender_name || '成员'}
                                  src={message.sender_avatar_url}
                                  size={28}
                                />
                                <strong>{mine ? '我' : message.sender_name || '成员'}</strong>
                                <time>{datetime(message.created_at)}</time>
                              </div>
                              {message.content &&
                              !message.attachments.some(
                                (file) => file.file_name === message.content,
                              ) ? (
                                <p>{message.content}</p>
                              ) : null}
                              {message.attachments.map((file) => (
                                <CommunicationAttachment
                                  key={file.id}
                                  file={file}
                                  previewPath={`/communication-files/${file.id}/preview`}
                                  thumbnailPath={`/communication-files/${file.id}/thumbnail`}
                                  downloadPath={`/communication-files/${file.id}/download`}
                                  withOrganization
                                />
                              ))}
                            </article>
                          );
                        })
                      ) : (
                        <div className="communication-empty">
                          发送第一条消息，开启本次业务沟通。
                        </div>
                      )}
                    </div>
                  </QueryState>
                  <form className="communication-composer" onSubmit={send}>
                    <label className="btn btn-secondary communication-upload">
                      <Upload size={16} />
                      {uploading ? '上传中…' : '附件'}
                      <input
                        type="file"
                        disabled={uploading || sending}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          void sendAttachment(file);
                          event.currentTarget.value = '';
                        }}
                      />
                    </label>
                    <input
                      className="input grow"
                      value={text}
                      onChange={(event) => setText(event.target.value)}
                      placeholder="输入消息…"
                      maxLength={8000}
                      aria-label="消息内容"
                    />
                    <Button loading={sending} disabled={uploading || !text.trim()}>
                      <Send size={16} /> 发送
                    </Button>
                  </form>
                </>
              ) : (
                <div className="communication-unavailable">
                  <Button
                    variant="ghost"
                    className="communication-back"
                    onClick={() => updateParams({ conversation: '' })}
                  >
                    <ArrowLeft size={17} />
                    返回会话
                  </Button>
                  <Empty
                    title={
                      selectedID
                        ? detail.isPending
                          ? '正在打开会话…'
                          : '暂时无法打开会话'
                        : '选择一个会话，开始沟通'
                    }
                    description={
                      selectedID
                        ? '请重试，或返回列表选择其他会话。'
                        : '可按会话名称搜索项目、需求和资源沟通。'
                    }
                  />
                  <ErrorNotice error={selectedID ? detail.error : null} />
                  {selectedID && detail.isError && (
                    <Button variant="secondary" onClick={() => void detail.refetch()}>
                      重新加载
                    </Button>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </QueryState>
    </>
  );
}
